<template>
  <!-- 설치 장소 생성·수정 모달. mode 로 필드 묶음을 고른다:
       create = 전체, sip = SIP 서버 섹션, ips = 공인 IP 섹션.
       호출은 부모가 한다(submit 이벤트) — 실패 문구는 error 로 받아 모달 상단에 보인다. -->
  <a-modal :open="open" :footer="null" :mask-closable="false" width="520px" @cancel="emit('close')">
    <template #title>
      <div class="modal-title">{{ TITLES[mode] }}</div>
    </template>

    <div class="modal-content">
      <div class="pbx-site-form">
        <a-alert v-if="shownError" type="error" show-icon :message="shownError" />

        <template v-if="mode === 'create'">
          <label class="pbx-site-form__field">
            <span class="pbx-site-form__label">이름 <em>*</em></span>
            <a-input v-model:value="form.name" :maxlength="100" placeholder="예: 강남 본원" />
          </label>
        </template>

        <template v-if="mode !== 'ips'">
          <div v-if="mode === 'sip' && site" class="pbx-site-form__field">
            <span class="pbx-site-form__label">SIP 서버</span>
            <span class="pbx-site-form__readonly">{{ site.sip_server_fqdn }}</span>
            <span class="pbx-site-form__hint">{{ PBX_SITE_FQDN_HINT }}</span>
          </div>

          <div class="pbx-site-form__field">
            <span class="pbx-site-form__label">기본 전송</span>
            <a-radio-group v-model:value="form.transport">
              <a-radio value="tls">TLS 5061</a-radio>
              <a-radio value="udp">UDP 5060</a-radio>
            </a-radio-group>
          </div>

          <div class="pbx-site-form__field">
            <!-- SRTP 는 TLS 위에서만 의미가 있다(UDP 는 키가 평문으로 오간다) — UDP 면 끈다 -->
            <a-checkbox v-model:checked="form.srtp" :disabled="form.transport === 'udp'">SRTP 필수</a-checkbox>
            <span class="pbx-site-form__hint">PBX 전환기 설정과 함께 켭니다 — 지금은 해제 상태가 기본</span>
          </div>

          <label class="pbx-site-form__field">
            <span class="pbx-site-form__label">NTP 서버</span>
            <a-input v-model:value="form.ntp" :maxlength="253" :placeholder="emptyKeepsHint" />
          </label>

          <div class="pbx-site-form__row">
            <label class="pbx-site-form__field">
              <span class="pbx-site-form__label">시간대 오프셋(초)</span>
              <a-input-number
                v-model:value="form.offset"
                class="pbx-site-form__number"
                :min="GMT_OFFSET_MIN"
                :max="GMT_OFFSET_MAX"
                :precision="0"
                :placeholder="emptyKeepsHint"
              />
              <span class="pbx-site-form__hint">32400 = KST(UTC+9)</span>
            </label>

            <label class="pbx-site-form__field">
              <span class="pbx-site-form__label">설정 수신 주기(초)</span>
              <a-input-number
                v-model:value="form.polling"
                class="pbx-site-form__number"
                :min="1"
                :precision="0"
                :placeholder="mode === 'create' ? '비워 두면 서버 기본값' : emptyKeepsHint"
              />
            </label>
          </div>
        </template>

        <template v-if="mode !== 'sip'">
          <div class="pbx-site-form__field">
            <span class="pbx-site-form__label">
              공인 IP 목록<template v-if="mode === 'create'"> (선택)</template>
            </span>
            <a-select
              v-model:value="form.ips"
              mode="tags"
              :open="false"
              :token-separators="IP_SEPARATORS"
              placeholder="IP 를 입력하고 Enter 또는 쉼표"
            />
            <span class="pbx-site-form__hint">진단·경고용 — NSG 의 원천이 아님</span>
          </div>

          <div class="pbx-site-form__field">
            <a-checkbox v-model:checked="form.restrict">설치 장소 IP 에서만 설정 허용</a-checkbox>
            <span class="pbx-site-form__hint">고정 IP 를 쓰는 설치 장소만 켭니다. 목록 밖 IP 에서 온 전화기는 설정을 받지 못합니다.</span>
          </div>
        </template>
      </div>

      <div class="modal-footer">
        <a-button @click="emit('close')">취소</a-button>
        <a-button type="primary" class="yellow-button" :disabled="!canSubmit" :loading="submitting" @click="submit">
          {{ mode === 'create' ? '만들기' : '저장' }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';

import { defaultSipPort } from '@/views/Callbot/service/pbxExtensions';

/**
 * 요청 본문 규칙
 * - 생성: 화면에 기본값이 보이는 필드(전송·SRTP·NTP·오프셋·IP 제한)는 보이는 값을 그대로 보낸다 —
 *   서버 기본값과 어긋나도 사용자가 본 대로 만들어지게. 비워 둔 필드(주기·NTP·오프셋·IP 목록)는
 *   아예 빼서 서버 기본값을 따른다. null 은 보내지 않는다.
 * - 수정: 바뀐 필드만 보낸다. 비운 칸은 "바꾸지 않음" 이다(PATCH 에 null 을 싣지 않는다).
 * - 포트는 입력받지 않고 전송에서 정한다(TLS 5061 / UDP 5060) — 전송을 바꾸면 포트도 같이 보낸다.
 * 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "화면 명세 › 설치 장소"
 */

/** mode: 'create' | 'sip' | 'ips' */
const TITLES = {
  create: '설치 장소 추가',
  sip: 'SIP 서버 설정 수정',
  ips: '공인 IP 수정',
};

const PBX_SITE_FQDN_HINT = 'PBX 호스트의 속성이라 설치 장소에서 바꾸지 않습니다';
const DEFAULT_NTP = 'time.google.com';
const DEFAULT_GMT_OFFSET = 32400;
// 실제 시간대 범위(UTC-12 ~ UTC+14)
const GMT_OFFSET_MIN = -12 * 3600;
const GMT_OFFSET_MAX = 14 * 3600;
const IP_SEPARATORS = [',', ' ', '\n', ';'];

const props = defineProps({
  open: { type: Boolean, required: true },
  mode: { type: String, required: true },
  /** 수정 대상 — create 에서는 null */
  site: { type: Object, default: null },
  submitting: { type: Boolean, required: true },
  /** 서버 실패 문구(부모가 채운다) */
  error: { type: String, default: null },
});

const emit = defineEmits(['close', 'submitCreate', 'submitUpdate']);

const form = reactive(blankForm());
const localError = ref(null);

function blankForm() {
  return {
    name: '',
    transport: 'tls',
    srtp: false,
    ntp: DEFAULT_NTP,
    offset: DEFAULT_GMT_OFFSET,
    polling: null,
    ips: [],
    restrict: false,
  };
}

function formFromSite(site) {
  return {
    name: site.name,
    transport: site.sip_transport_default,
    srtp: site.srtp_require,
    ntp: site.ntp_server,
    offset: site.gmt_offset_sec,
    polling: site.polling_period_sec,
    ips: [...(site.public_ips ?? [])],
    restrict: site.restrict_provisioning_to_site_ips,
  };
}

// 열릴 때마다 초기화 — 닫았다 다시 열면 이전 입력·오류가 남지 않게
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    Object.assign(form, props.mode === 'create' || !props.site ? blankForm() : formFromSite(props.site));
    localError.value = null;
  },
  { immediate: true }
);

watch(
  () => form.transport,
  (t) => {
    if (t === 'udp') form.srtp = false;
  }
);

// 입력 검증 오류가 먼저 — 서버 오류는 부모가 다음 제출 때 비운다
const shownError = computed(() => localError.value ?? props.error);

const emptyKeepsHint = computed(() =>
  props.mode === 'create' ? '비워 두면 서버 기본값' : '비워 두면 바꾸지 않습니다'
);

/** 공인 IP — 앞뒤 공백 제거, 빈 값·중복 제거. 서버가 최종 검증한다 */
function normalizedIps() {
  const out = [];
  for (const raw of form.ips) {
    const v = String(raw).trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

const IPV4_CIDR = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}(\/(3[0-2]|[12]?\d))?$/;
const IPV6_CIDR = /^[0-9a-fA-F:]*:[0-9a-fA-F:.]*(\/(12[0-8]|1[01]\d|\d?\d))?$/;

function isIpLike(v) {
  return IPV4_CIDR.test(v) || IPV6_CIDR.test(v);
}

function sameList(a, b) {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** 수정 모드에서 바뀐 필드만 — 비운 칸은 바꾸지 않음 */
function buildUpdate(site) {
  const out = {};
  if (props.mode === 'sip') {
    if (form.transport !== site.sip_transport_default) {
      out.sip_transport_default = form.transport;
      out.sip_port_default = defaultSipPort(form.transport);
    }
    if (form.srtp !== site.srtp_require) out.srtp_require = form.srtp;
    const ntp = form.ntp.trim();
    if (ntp && ntp !== site.ntp_server) out.ntp_server = ntp;
    if (form.offset != null && form.offset !== site.gmt_offset_sec) out.gmt_offset_sec = form.offset;
    if (form.polling != null && form.polling !== site.polling_period_sec) out.polling_period_sec = form.polling;
  } else if (props.mode === 'ips') {
    const ips = normalizedIps();
    if (!sameList(ips, site.public_ips ?? [])) out.public_ips = ips;
    if (form.restrict !== site.restrict_provisioning_to_site_ips) {
      out.restrict_provisioning_to_site_ips = form.restrict;
    }
  }
  return out;
}

function buildCreate() {
  const out = {
    name: form.name.trim(),
    sip_transport_default: form.transport,
    sip_port_default: defaultSipPort(form.transport),
    srtp_require: form.srtp,
    restrict_provisioning_to_site_ips: form.restrict,
  };
  const ntp = form.ntp.trim();
  if (ntp) out.ntp_server = ntp;
  if (form.offset != null) out.gmt_offset_sec = form.offset;
  if (form.polling != null) out.polling_period_sec = form.polling;
  const ips = normalizedIps();
  if (ips.length) out.public_ips = ips;
  return out;
}

const canSubmit = computed(() => {
  if (props.submitting) return false;
  if (props.mode === 'create') return !!form.name.trim();
  if (!props.site) return false;
  // 바뀐 것이 없으면 저장할 게 없다
  return Object.keys(buildUpdate(props.site)).length > 0;
});

function submit() {
  if (!canSubmit.value) return;
  localError.value = null;
  if (props.mode !== 'sip') {
    const bad = normalizedIps().filter((v) => !isIpLike(v));
    if (bad.length) {
      localError.value = `공인 IP 형식이 올바르지 않습니다: ${bad.join(', ')}`;
      return;
    }
  }
  if (props.mode === 'create') emit('submitCreate', buildCreate());
  else if (props.site) emit('submitUpdate', buildUpdate(props.site));
}
</script>

<style scoped lang="scss">
.modal-title {
  font-size: 18px;
  font-weight: 700;
}

// 전역 Bootstrap 의 .modal-content 규칙(테두리·둥근 모서리·flex)을 끄고 여백을 준다
.modal-content {
  display: block;
  padding: 4px 24px 0;
  border: none;
  border-radius: 0;
  background: transparent;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 0 20px;
  border-top: 1px solid #e9ecef;
  margin-top: 16px;
}

.pbx-site-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
  font-size: 13px;

  &__row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: #262626;

    em {
      font-style: normal;
      color: #cf1322;
    }
  }

  &__readonly {
    padding: 6px 10px;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    background: #fafafa;
    color: #595959;
    word-break: break-all;
  }

  &__number {
    width: 100%;
  }

  &__hint {
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.5;
  }
}

/* 전역 정의가 없는 클래스라 쓰는 컴포넌트마다 들고 있어야 한다. */
:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
